import { AnimatedCollapse } from '@backrs_ui/common-ui/src/atoms/AnimatedCollapse';
import { AppIcon } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';
import { BackrsUser } from '@backrs_ui/common-ui/src/models/user';

import { Box, SxProps, Typography, Theme, Avatar } from '@mui/material';

import { SIDEBAR_EXPANDED_WIDTH } from './BackrsSidebar';

type ProfileWidgetProps = Readonly<{
  backrsUser: BackrsUser;
  isSidebarOpen: boolean;
  openProfileMenu: () => void;
}>;

export const ProfileWidget = ({
  backrsUser,
  isSidebarOpen,
  openProfileMenu,
}: ProfileWidgetProps) => {
  return (
    <Box sx={profileWidgetStyles(isSidebarOpen)}>
      <Box sx={profileImageNameContainer}>
        {/* PROFILE IMAGE */}
        <Avatar
          sx={profileImageContainer}
          src={backrsUser.profile_image}
          alt={`${backrsUser.first_name}${backrsUser.last_name}`}
          onClick={isSidebarOpen ? undefined : openProfileMenu}
        >
          <Box sx={fallbackProfileImageContainer}>
            <AppIcon iconName="User01" diameter="24px" color={'Colors/Foreground/fg-quaternary'} />
          </Box>
        </Avatar>

        {/* PROFILE NAME */}
        <AnimatedCollapse isOpen={isSidebarOpen} direction="horizontal" opacityFade>
          <Box sx={profileNameContainer(isSidebarOpen)}>
            <Typography variant="text-sm-semibold" sx={profileNameTextSx}>
              {backrsUser.first_name} {backrsUser.last_name}
            </Typography>
            <Typography sx={emailStyle} variant="text-sm-regular">
              {backrsUser.email}
            </Typography>
          </Box>
        </AnimatedCollapse>
      </Box>

      {/* CHEVRON ICON */}
      <AnimatedCollapse isOpen={isSidebarOpen} direction="horizontal" opacityFade>
        <Box
          className="profile-widget-chevron"
          sx={dropDownStyles(isSidebarOpen)}
          onClick={openProfileMenu}
        >
          <AppIcon
            iconName="ChevronSelectorVertical"
            diameter="20px"
            color={'Colors/Foreground/fg-quinary'}
          />
        </Box>
      </AnimatedCollapse>
    </Box>
  );
};

const profileWidgetStyles =
  (isSidebarOpen: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: isSidebarOpen ? backrs.spacing['spacing-xl'] : 0,
    paddingLeft: isSidebarOpen ? backrs.spacing['spacing-lg'] : backrs.spacing['spacing-xs'],
    paddingRight: isSidebarOpen ? backrs.spacing['spacing-lg'] : backrs.spacing['spacing-none'],
    paddingY: isSidebarOpen ? backrs.spacing['spacing-lg'] : backrs.spacing['spacing-none'],

    borderRadius: backrs.radius['radius-xl'],
    border: `1px solid ${isSidebarOpen ? backrs.colors['Colors/Foreground/fg-primary'] : 'transparent'}`,
    boxShadow: backrs.shadows['shadow-xs'],
    justifyContent: 'space-between',
    transition: 'all 0.3s ease-in-out',
  });

const profileImageContainer: SxProps<Theme> = ({ backrs }) => ({
  width: '40px',
  height: '40px',
  borderRadius: '200px',
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  transition: 'border 0.3s ease-in-out',
});

const fallbackProfileImageContainer: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Colors/Background/bg-tertiary'],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
  borderRadius: '200px',
});

const profileNameContainer =
  (isSidebarOpen: boolean): SxProps<Theme> =>
  () => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    flexWrap: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    minWidth: isSidebarOpen ? `${SIDEBAR_EXPANDED_WIDTH / 2.2}px` : 'unset',
  });

const profileNameTextSx: SxProps<Theme> = {
  flexDirection: 'column',
  justifyContent: 'flex-start',
  flexWrap: 'nowrap',
  textOverflow: 'ellipsis',
} as const;

const profileImageNameContainer: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 2,
  width: '100%',
  flexWrap: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const emailStyle: SxProps<Theme> = ({ backrs }) => ({
  overflow: 'hidden',
  flexWrap: 'nowrap',
  textOverflow: 'ellipsis',
  color: backrs.colors['Colors/Text/text-tertiary'],
});

const dropDownStyles =
  (isSidebarOpen: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    cursor: 'pointer',
    padding: backrs.spacing['spacing-sm'],
    paddingTop: 0,
    borderRadius: backrs.radius['radius-sm'],
    opacity: isSidebarOpen ? 1 : 0,
    transition: 'all 0.3s ease-in-out',
  });
